/**
 * SVG renderers for each annotation type.
 * Follows ASME Y14.2 line conventions and Y14.5 dimension styling.
 */
import type {
  Annotation, LinearDimension, RadialDimension, DiameterDimension,
  AngularDimension, OrdinateDimension, CenterlineMark, LeaderNote,
  Balloon, TextNote, GdtFrame, SurfaceFinish, Vec2,
} from "./annotationTypes";
import { dist2d } from "./annotationTypes";

// ── Shared constants (mm, matching A4 sheet scale) ─────────

const ARROW_L = 2.5;     // arrowhead length
const ARROW_W = 0.8;     // arrowhead half-width
const EXT_GAP = 0.8;     // gap between geometry and extension line
const EXT_OVERSHOOT = 1.5;
const DIM_STROKE = 0.3;
const DIM_FONT = 3;
const DIM_FONT_FAMILY = "'DM Mono', monospace";
const DIM_COLOR = "#000"; // dimension lines, text, arrowheads

// ── Arrowhead ─────────────────────────────────────────────

function Arrowhead({ tip, angleDeg }: { tip: Vec2; angleDeg: number }) {
  const rad = (angleDeg * Math.PI) / 180;
  const dx = Math.cos(rad);
  const dy = Math.sin(rad);
  const px = -dx * ARROW_L + dy * ARROW_W;
  const py = -dy * ARROW_L - dx * ARROW_W;
  const qx = -dx * ARROW_L - dy * ARROW_W;
  const qy = -dy * ARROW_L + dx * ARROW_W;
  return (
    <polygon
      points={`${tip[0]},${tip[1]} ${tip[0] + px},${tip[1] + py} ${tip[0] + qx},${tip[1] + qy}`}
      fill={DIM_COLOR}
    />
  );
}

// ── Linear Dimension ──────────────────────────────────────

function LinearDimensionSvg({ d, selected }: { d: LinearDimension; selected: boolean }) {
  const { p1, p2, offset, orientation } = d;

  let dimP1: Vec2, dimP2: Vec2;
  if (orientation === "horizontal") {
    dimP1 = [p1[0], p1[1] - offset];
    dimP2 = [p2[0], p1[1] - offset];
  } else if (orientation === "vertical") {
    dimP1 = [p1[0] - offset, p1[1]];
    dimP2 = [p1[0] - offset, p2[1]];
  } else {
    // aligned
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len * offset;
    const ny = dx / len * offset;
    dimP1 = [p1[0] + nx, p1[1] + ny];
    dimP2 = [p2[0] + nx, p2[1] + ny];
  }

  const distance = dist2d(dimP1, dimP2);
  const angle = Math.atan2(dimP2[1] - dimP1[1], dimP2[0] - dimP1[0]) * 180 / Math.PI;
  const midX = (dimP1[0] + dimP2[0]) / 2;
  const midY = (dimP1[1] + dimP2[1]) / 2;

  const value = d.textOverride || distance.toFixed(1);
  let toleranceText = "";
  if (d.tolerance) {
    const t = d.tolerance;
    if (t.type === "symmetric") toleranceText = ` ±${t.upper}`;
    else if (t.type === "deviation") toleranceText = ` +${t.upper}/-${t.lower}`;
    else toleranceText = ` ${(distance - t.lower).toFixed(1)}–${(distance + t.upper).toFixed(1)}`;
  }

  return (
    <g className={`annotation-dim ${selected ? "annotation-selected" : ""}`}>
      {/* Extension lines */}
      <line x1={p1[0]} y1={p1[1] + (offset > 0 ? -EXT_GAP : EXT_GAP)} x2={dimP1[0]} y2={dimP1[1] - (offset > 0 ? -EXT_OVERSHOOT : EXT_OVERSHOOT)} stroke={DIM_COLOR} strokeWidth={DIM_STROKE * 0.7} />
      <line x1={p2[0]} y1={p2[1] + (offset > 0 ? -EXT_GAP : EXT_GAP)} x2={dimP2[0]} y2={dimP2[1] - (offset > 0 ? -EXT_OVERSHOOT : EXT_OVERSHOOT)} stroke={DIM_COLOR} strokeWidth={DIM_STROKE * 0.7} />
      {/* Dimension line */}
      <line x1={dimP1[0]} y1={dimP1[1]} x2={dimP2[0]} y2={dimP2[1]} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      {/* Arrowheads */}
      <Arrowhead tip={dimP1} angleDeg={angle} />
      <Arrowhead tip={dimP2} angleDeg={angle + 180} />
      {/* Text */}
      <text
        x={midX}
        y={midY - 1}
        textAnchor="middle"
        fontSize={DIM_FONT}
        fontFamily={DIM_FONT_FAMILY}
        fill={DIM_COLOR}
        transform={`rotate(${Math.abs(angle) > 90 ? angle + 180 : angle}, ${midX}, ${midY - 1})`}
      >
        {value}{toleranceText}
      </text>
    </g>
  );
}

// ── Radial Dimension ──────────────────────────────────────

function RadialDimensionSvg({ d, selected }: { d: RadialDimension; selected: boolean }) {
  const rad = (d.leaderAngle * Math.PI) / 180;
  const rimX = d.center[0] + d.radius * Math.cos(rad);
  const rimY = d.center[1] + d.radius * Math.sin(rad);
  const textX = d.center[0] + (d.radius + 5) * Math.cos(rad);
  const textY = d.center[1] + (d.radius + 5) * Math.sin(rad);

  return (
    <g className={`annotation-dim ${selected ? "annotation-selected" : ""}`}>
      <line x1={d.center[0]} y1={d.center[1]} x2={rimX} y2={rimY} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <Arrowhead tip={[rimX, rimY]} angleDeg={d.leaderAngle} />
      <text x={textX} y={textY} textAnchor="middle" fontSize={DIM_FONT} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR}>
        R{d.textOverride || d.radius.toFixed(1)}
      </text>
    </g>
  );
}

// ── Diameter Dimension ────────────────────────────────────

function DiameterDimensionSvg({ d, selected }: { d: DiameterDimension; selected: boolean }) {
  const rad = (d.leaderAngle * Math.PI) / 180;
  const r = d.diameter / 2;
  const p1x = d.center[0] - r * Math.cos(rad);
  const p1y = d.center[1] - r * Math.sin(rad);
  const p2x = d.center[0] + r * Math.cos(rad);
  const p2y = d.center[1] + r * Math.sin(rad);
  const textX = d.center[0] + (r + 5) * Math.cos(rad);
  const textY = d.center[1] + (r + 5) * Math.sin(rad);

  return (
    <g className={`annotation-dim ${selected ? "annotation-selected" : ""}`}>
      <line x1={p1x} y1={p1y} x2={p2x} y2={p2y} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <Arrowhead tip={[p1x, p1y]} angleDeg={d.leaderAngle + 180} />
      <Arrowhead tip={[p2x, p2y]} angleDeg={d.leaderAngle} />
      <text x={textX} y={textY} textAnchor="middle" fontSize={DIM_FONT} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR}>
        ⌀{d.textOverride || d.diameter.toFixed(1)}
      </text>
    </g>
  );
}

// ── Angular Dimension ─────────────────────────────────────

function AngularDimensionSvg({ d, selected }: { d: AngularDimension; selected: boolean }) {
  const a1 = Math.atan2(d.p1[1] - d.vertex[1], d.p1[0] - d.vertex[0]);
  const a2 = Math.atan2(d.p2[1] - d.vertex[1], d.p2[0] - d.vertex[0]);
  const startDeg = (a1 * 180) / Math.PI;
  const endDeg = (a2 * 180) / Math.PI;
  let sweep = endDeg - startDeg;
  if (sweep < 0) sweep += 360;
  const largeArc = sweep > 180 ? 1 : 0;

  const r = d.arcRadius;
  const sx = d.vertex[0] + r * Math.cos(a1);
  const sy = d.vertex[1] + r * Math.sin(a1);
  const ex = d.vertex[0] + r * Math.cos(a2);
  const ey = d.vertex[1] + r * Math.sin(a2);
  const midAngle = a1 + (sweep * Math.PI) / 360;
  const tx = d.vertex[0] + (r + 2) * Math.cos(midAngle);
  const ty = d.vertex[1] + (r + 2) * Math.sin(midAngle);

  return (
    <g className={`annotation-dim ${selected ? "annotation-selected" : ""}`}>
      {/* Extension lines from vertex */}
      <line x1={d.vertex[0]} y1={d.vertex[1]} x2={sx} y2={sy} stroke={DIM_COLOR} strokeWidth={DIM_STROKE * 0.7} strokeDasharray="1 0.5" />
      <line x1={d.vertex[0]} y1={d.vertex[1]} x2={ex} y2={ey} stroke={DIM_COLOR} strokeWidth={DIM_STROKE * 0.7} strokeDasharray="1 0.5" />
      {/* Arc */}
      <path
        d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`}
        fill="none"
        stroke={DIM_COLOR}
        strokeWidth={DIM_STROKE}
      />
      <Arrowhead tip={[sx, sy]} angleDeg={startDeg - 90} />
      <Arrowhead tip={[ex, ey]} angleDeg={endDeg + 90} />
      <text x={tx} y={ty} textAnchor="middle" fontSize={DIM_FONT} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR}>
        {d.textOverride || `${sweep.toFixed(1)}°`}
      </text>
    </g>
  );
}

// ── Ordinate Dimension ────────────────────────────────────

function OrdinateDimensionSvg({ d, selected }: { d: OrdinateDimension; selected: boolean }) {
  const val = d.axis === "x" ? Math.abs(d.point[0] - d.origin[0]) : Math.abs(d.point[1] - d.origin[1]);
  const jogX = d.axis === "x" ? d.point[0] : d.point[0] + d.jogOffset;
  const jogY = d.axis === "y" ? d.point[1] : d.point[1] - d.jogOffset;

  return (
    <g className={`annotation-dim ${selected ? "annotation-selected" : ""}`}>
      <line x1={d.point[0]} y1={d.point[1]} x2={jogX} y2={jogY} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <text x={jogX} y={jogY - 1.5} textAnchor="middle" fontSize={DIM_FONT} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR}>
        {val.toFixed(1)}
      </text>
    </g>
  );
}

// ── Centerline Mark ───────────────────────────────────────

function CenterlineMarkSvg({ d, selected }: { d: CenterlineMark; selected: boolean }) {
  if (d.style === "cross") {
    const a = d.armLength;
    return (
      <g className={`annotation-centerline ${selected ? "annotation-selected" : ""}`}>
        <line x1={d.p1[0] - a} y1={d.p1[1]} x2={d.p1[0] + a} y2={d.p1[1]} stroke={DIM_COLOR} strokeWidth={DIM_STROKE * 0.6} strokeDasharray="4 1 1 1" />
        <line x1={d.p1[0]} y1={d.p1[1] - a} x2={d.p1[0]} y2={d.p1[1] + a} stroke={DIM_COLOR} strokeWidth={DIM_STROKE * 0.6} strokeDasharray="4 1 1 1" />
      </g>
    );
  }
  // line style
  return (
    <g className={`annotation-centerline ${selected ? "annotation-selected" : ""}`}>
      <line x1={d.p1[0]} y1={d.p1[1]} x2={d.p2![0]} y2={d.p2![1]} stroke={DIM_COLOR} strokeWidth={DIM_STROKE * 0.6} strokeDasharray="8 2 2 2" />
    </g>
  );
}

// ── Leader Note ───────────────────────────────────────────

function LeaderNoteSvg({ d, selected }: { d: LeaderNote; selected: boolean }) {
  if (d.points.length < 2) return null;
  const pathData = d.points.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  const last = d.points[d.points.length - 1];
  const prev = d.points[d.points.length - 2];
  const angle = Math.atan2(last[1] - prev[1], last[0] - prev[0]) * 180 / Math.PI;

  return (
    <g className={`annotation-leader ${selected ? "annotation-selected" : ""}`}>
      <path d={pathData} fill="none" stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <Arrowhead tip={last} angleDeg={angle} />
      <text x={d.points[0][0]} y={d.points[0][1] - 1.5} fontSize={d.fontSize} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR}>
        {d.text}
      </text>
    </g>
  );
}

// ── Balloon ───────────────────────────────────────────────

function BalloonSvg({ d, selected }: { d: Balloon; selected: boolean }) {
  const angle = Math.atan2(d.leaderTip[1] - d.center[1], d.leaderTip[0] - d.center[0]);
  const edgeX = d.center[0] + d.radius * Math.cos(angle);
  const edgeY = d.center[1] + d.radius * Math.sin(angle);

  return (
    <g className={`annotation-balloon ${selected ? "annotation-selected" : ""}`}>
      <circle cx={d.center[0]} cy={d.center[1]} r={d.radius} fill="none" stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <line x1={edgeX} y1={edgeY} x2={d.leaderTip[0]} y2={d.leaderTip[1]} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <Arrowhead tip={d.leaderTip} angleDeg={angle * 180 / Math.PI} />
      <text x={d.center[0]} y={d.center[1] + 1} textAnchor="middle" fontSize={DIM_FONT} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR} fontWeight="bold">
        {d.itemNumber}
      </text>
    </g>
  );
}

// ── Text Note ─────────────────────────────────────────────

function TextNoteSvg({ d, selected }: { d: TextNote; selected: boolean }) {
  return (
    <g className={`annotation-text ${selected ? "annotation-selected" : ""}`}>
      <text
        x={d.position[0]}
        y={d.position[1]}
        fontSize={d.fontSize}
        fontFamily={DIM_FONT_FAMILY}
        fontWeight={d.fontWeight}
        fill={DIM_COLOR}
        transform={d.rotation ? `rotate(${d.rotation}, ${d.position[0]}, ${d.position[1]})` : undefined}
      >
        {d.text}
      </text>
    </g>
  );
}

// ── GD&T Frame ────────────────────────────────────────────

const GDT_SYMBOLS: Record<string, string> = {
  straightness: "⏤", flatness: "⏥", circularity: "○", cylindricity: "⌭",
  perpendicularity: "⊥", angularity: "∠", parallelism: "∥",
  position: "⌖", concentricity: "◎", symmetry: "⌯",
  profileLine: "⌒", profileSurface: "⌓",
  circularRunout: "↗", totalRunout: "↗↗",
};

function GdtFrameSvg({ d, selected }: { d: GdtFrame; selected: boolean }) {
  const cellW = 12;
  const cellH = 6;
  const cells = [GDT_SYMBOLS[d.characteristic] || "?", d.toleranceValue + (d.modifier ? ` Ⓜ` : "")];
  if (d.datumA) cells.push(d.datumA);
  if (d.datumB) cells.push(d.datumB);
  if (d.datumC) cells.push(d.datumC);
  const totalW = cells.length * cellW;

  const angle = Math.atan2(d.leaderTip[1] - d.position[1], d.leaderTip[0] - d.position[0]) * 180 / Math.PI;

  return (
    <g className={`annotation-gdt ${selected ? "annotation-selected" : ""}`}>
      {/* Leader */}
      <line x1={d.position[0]} y1={d.position[1] + cellH / 2} x2={d.leaderTip[0]} y2={d.leaderTip[1]} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <Arrowhead tip={d.leaderTip} angleDeg={angle} />
      {/* Frame border */}
      <rect x={d.position[0]} y={d.position[1]} width={totalW} height={cellH} fill="#fff" stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      {/* Cell dividers + text */}
      {cells.map((txt, i) => (
        <g key={i}>
          {i > 0 && (
            <line x1={d.position[0] + i * cellW} y1={d.position[1]} x2={d.position[0] + i * cellW} y2={d.position[1] + cellH} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
          )}
          <text
            x={d.position[0] + i * cellW + cellW / 2}
            y={d.position[1] + cellH / 2 + 1}
            textAnchor="middle"
            fontSize={2.8}
            fontFamily={DIM_FONT_FAMILY}
            fill={DIM_COLOR}
          >
            {txt}
          </text>
        </g>
      ))}
    </g>
  );
}

// ── Surface Finish ────────────────────────────────────────

function SurfaceFinishSvg({ d, selected }: { d: SurfaceFinish; selected: boolean }) {
  const { position: p, leaderTip } = d;
  const angle = Math.atan2(leaderTip[1] - p[1], leaderTip[0] - p[0]) * 180 / Math.PI;
  // Standard check-mark symbol shape
  const symbolPath = `M ${p[0]} ${p[1]} l 2 -5 l 2 5 l 8 0`;

  return (
    <g className={`annotation-surface ${selected ? "annotation-selected" : ""}`}>
      <line x1={p[0]} y1={p[1]} x2={leaderTip[0]} y2={leaderTip[1]} stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <Arrowhead tip={leaderTip} angleDeg={angle} />
      <path d={symbolPath} fill="none" stroke={DIM_COLOR} strokeWidth={DIM_STROKE} />
      <text x={p[0] + 3} y={p[1] - 6} fontSize={2.5} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR}>
        {d.roughness}
      </text>
      {d.process && (
        <text x={p[0] + 3} y={p[1] - 9} fontSize={2} fontFamily={DIM_FONT_FAMILY} fill={DIM_COLOR}>
          {d.process}
        </text>
      )}
    </g>
  );
}

// ── Main dispatcher ───────────────────────────────────────

interface AnnotationRendererProps {
  annotation: Annotation;
  selected: boolean;
  /** Translation offset for the owning view quadrant */
  offsetX: number;
  offsetY: number;
  onClick?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RENDERERS: Record<string, (d: any, sel: boolean) => JSX.Element | null> = {
  linear: (d, s) => <LinearDimensionSvg d={d} selected={s} />,
  radial: (d, s) => <RadialDimensionSvg d={d} selected={s} />,
  diameter: (d, s) => <DiameterDimensionSvg d={d} selected={s} />,
  angular: (d, s) => <AngularDimensionSvg d={d} selected={s} />,
  ordinate: (d, s) => <OrdinateDimensionSvg d={d} selected={s} />,
  centerline: (d, s) => <CenterlineMarkSvg d={d} selected={s} />,
  leader: (d, s) => <LeaderNoteSvg d={d} selected={s} />,
  balloon: (d, s) => <BalloonSvg d={d} selected={s} />,
  text: (d, s) => <TextNoteSvg d={d} selected={s} />,
  gdt: (d, s) => <GdtFrameSvg d={d} selected={s} />,
  surfaceFinish: (d, s) => <SurfaceFinishSvg d={d} selected={s} />,
};

export default function AnnotationRenderer({ annotation, selected, offsetX, offsetY, onClick }: AnnotationRendererProps) {
  const render = RENDERERS[annotation.kind];
  if (!render) return null;

  return (
    <g
      transform={`translate(${offsetX}, ${offsetY})`}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      style={{ cursor: "pointer" }}
    >
      {render(annotation, selected)}
    </g>
  );
}
